/** Review hints are deliberately separate from verified public facts. */
export const ATTRIBUTE_LABELS = {
    landDimensions: "Land width × length",
    landArea: "Land area",
    buildingDimensions: "Building width × length",
    floors: "Floors",
    bedrooms: "Bedrooms",
    masterBedrooms: "Master bedrooms",
    bathrooms: "Bathrooms",
    ownership: "Land ownership / type",
    facing: "Facing direction",
    parking: "Parking",
    water: "Water",
    electricity: "Electricity",
    township: "Township",
    neighborhood: "Neighborhood",
};
export function sourceText(html) {
    return html
        .replace(/<[^>]*>/g, "\n")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .trim();
}
export function soldEvidence(title, html) {
    const text = sourceText(title + "\n" + html);
    const match = /sold\s*[_-]?\s*out|ရောင်းပြီး(?:ပါပြီ)?|အရောင်းကုန်|ရောင်းချပြီး/iu.exec(text);
    return match
        ? text
            .slice(Math.max(0, match.index - 45), match.index + match[0].length + 70)
            .trim()
        : null;
}
export function suggestAttributes(html) {
    const text = sourceText(html);
    const rules = [
        [
            "landDimensions",
            /(?:မြေအကျယ်|မြေကွက်အကျယ်|land\s*(?:size|dimensions))[^\n]{0,18}?[0-9၀-၉]+\s*(?:ပေ|ft|feet|')?\s*[x×*]\s*[0-9၀-၉]+\s*(?:ပေ|ft|feet|')?/giu,
        ],
        ["bedrooms", /[0-9၀-၉]+\s*(?:bedrooms?|အိပ်ခန်း)/giu],
        [
            "masterBedrooms",
            /[0-9၀-၉]+\s*(?:master\s*(?:bedrooms?|beds?)|မာစတာအိပ်ခန်း)/giu,
        ],
        ["bathrooms", /[0-9၀-၉]+\s*(?:bathrooms?|ရေချိုးခန်း)/giu],
        ["floors", /[0-9၀-၉]+\s*(?:ထပ်|storeys?|stories)/giu],
    ];
    return rules.flatMap(([key, regex]) => {
        const matches = [...text.matchAll(regex)];
        // Conflicts remain in the source; never choose one of several values.
        if (new Set(matches.map((m) => m[0])).size !== 1)
            return [];
        const m = matches[0];
        if (!m)
            return [];
        return [
            {
                key,
                value: m[0],
                evidence: text
                    .slice(Math.max(0, m.index - 30), m.index + m[0].length + 30)
                    .trim(),
                confidence: "review_required",
            },
        ];
    });
}
export function displayPropertyTitle(p) {
    if (p.displayTitle?.trim())
        return p.displayTitle.trim();
    const source = p.title.replace(/\p{Extended_Pictographic}/gu, "").trim();
    if (source.length <= 90 &&
        source &&
        !/မိတ်ဆွေ|ရှာနေ|ဝယ်လို|လိုချင်|…|\.\s*\.\s*\.|^#?sold[\s_-]*out\b|^Triple H Property$/i.test(source))
        return source.replace(/\p{Extended_Pictographic}/gu, "").trim();
    const location = p.locationText.trim();
    if (location && location.length <= 80)
        return `${location.replace(/\p{Extended_Pictographic}/gu, "").trim()} · ${{ Houses: "အိမ်", Land: "မြေ", Commercial: "စီးပွားရေး အဆောက်အအုံ", Condos: "ကွန်ဒို" }[p.categoryName || ""] || p.categoryName || "အိမ်ခြံမြေ"} အရောင်း`;
    // A conservative presentation fallback, never an inferred property attribute.
    return `${p.categoryName || "အိမ်ခြံမြေ"} အရောင်း · Triple H`;
}
export function effectiveAvailability(p, now = Date.now()) {
    if (p.soldOut)
        return "sold";
    const time = Date.parse(p.lastAvailabilityConfirmedAt || "");
    return p.availabilityState === "available" &&
        Number.isFinite(time) &&
        time <= now &&
        now - time <= 180 * 86400000
        ? "available"
        : "needs_confirmation";
}
